import React, { useState, useCallback } from "react";
import Editor from "@monaco-editor/react";
import type {
  SDKConfiguration,
  ConfigurationFormProps,
  SingleAnimation,
} from "./types";

export const ConfigurationForm: React.FC<ConfigurationFormProps> = ({
  config,
  onConfigChange,
}) => {
  const [stylingError, setStylingError] = useState<string | null>(null);

  // JSON validation utility
  const validateJSON = useCallback(
    (jsonString: string): { valid: boolean; error?: string; data?: any } => {
      try {
        const parsed = JSON.parse(jsonString);
        return { valid: true, data: parsed };
      } catch (error) {
        return {
          valid: false,
          error: error instanceof Error ? error.message : "Invalid JSON",
        };
      }
    },
    []
  );

  const handleSimpleInputChange = (
    field: keyof SDKConfiguration,
    value: any
  ) => {
    onConfigChange({
      ...config,
      [field]: value,
    });
  };

  const handleNestedInputChange = (
    section: keyof SDKConfiguration,
    field: string,
    value: any
  ) => {
    onConfigChange({
      ...config,
      [section]: {
        ...(config[section] as any),
        [field]: value,
      },
    });
  };

  // Handle styling editor changes
  const handleStylingChange = useCallback(
    (value: string | undefined) => {
      if (!value) return;

      const validation = validateJSON(value);
      if (validation.valid && validation.data) {
        setStylingError(null);
        onConfigChange({
          ...config,
          styling: validation.data,
        });
      } else {
        setStylingError(validation.error || "Invalid JSON");
      }
    },
    [config, onConfigChange, validateJSON]
  );

  return (
    <div className="configuration-form">
      <div className="form-section">
        <h3>SDK параметры</h3>
        <div className="form-group">
          <label>
            Point ID <span className="required-field">*</span>:
            <input
              type="text"
              value={config.pointId}
              onChange={(e) =>
                handleSimpleInputChange("pointId", e.target.value)
              }
              className="form-input"
            />
          </label>
        </div>
        <div className="form-group">
          <label>
            Server URL <span className="required-field">*</span>:
            <input
              type="text"
              value={config.serverUrl}
              onChange={(e) =>
                handleSimpleInputChange("serverUrl", e.target.value)
              }
              className="form-input"
            />
          </label>
        </div>
        <div className="form-group">
          <label>
            Auth Code (код авторизации):
            <input
              type="text"
              value={config.authCode || ""}
              onChange={(e) =>
                handleSimpleInputChange("authCode", e.target.value || undefined)
              }
              className="form-input"
              placeholder="Код авторизации RestoPlace"
            />
          </label>
        </div>
        <div className="form-group">
          <label>
            Order ID (для редактирования заказа):
            <input
              type="text"
              value={config.orderId || ""}
              onChange={(e) =>
                handleSimpleInputChange("orderId", e.target.value || undefined)
              }
              className="form-input"
              placeholder="Оставьте пустым для создания нового заказа"
            />
          </label>
        </div>
        <div className="form-group">
          <label>
            Timeout (ms):
            <input
              type="number"
              value={config.timeout}
              onChange={(e) =>
                handleSimpleInputChange(
                  "timeout",
                  parseInt(e.target.value) || 0
                )
              }
              className="form-input"
            />
          </label>
        </div>
        <div className="form-group">
          <label>
            Debug:
            <input
              type="checkbox"
              checked={config.debug}
              onChange={(e) =>
                handleSimpleInputChange("debug", e.target.checked)
              }
              className="form-checkbox"
            />
          </label>
        </div>
        <div className="form-group">
          <label>
            isReadOnly (только просмотр корзины):
            <input
              type="checkbox"
              checked={config.isReadOnly}
              onChange={(e) =>
                handleSimpleInputChange("isReadOnly", e.target.checked)
              }
              className="form-checkbox"
            />
          </label>
        </div>
      </div>

      <div className="form-section">
        <h3>Настройки анимации</h3>
        <div className="form-group">
          <label>
            Включить анимации:
            <input
              type="checkbox"
              checked={config.animations.enabled}
              onChange={(e) =>
                handleNestedInputChange(
                  "animations",
                  "enabled",
                  e.target.checked
                )
              }
              className="form-checkbox"
            />
          </label>
        </div>

        <div style={{ marginTop: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <h4 style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>
              Анимации ({config.animations.animations.length})
            </h4>
            <button
              type="button"
              onClick={() => {
                const newAnimation: SingleAnimation = { type: "fade" };
                onConfigChange({
                  ...config,
                  animations: {
                    ...config.animations,
                    animations: [...config.animations.animations, newAnimation],
                  },
                });
              }}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              + Добавить анимацию
            </button>
          </div>

          {config.animations.animations.length === 0 && (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                color: "#6c757d",
                fontSize: "13px",
                textAlign: "center",
              }}
            >
              Нет анимаций. Добавьте первую анимацию.
            </div>
          )}

          {config.animations.animations.map((anim, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                padding: "16px",
                marginBottom: "12px",
                backgroundColor: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <strong style={{ fontSize: "13px" }}>
                  Анимация #{index + 1}
                </strong>
                <button
                  type="button"
                  onClick={() => {
                    const newAnimations = config.animations.animations.filter(
                      (_, i) => i !== index
                    );
                    onConfigChange({
                      ...config,
                      animations: {
                        ...config.animations,
                        animations: newAnimations,
                      },
                    });
                  }}
                  style={{
                    padding: "4px 8px",
                    fontSize: "11px",
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Удалить
                </button>
              </div>

              <div className="form-group" style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "12px", display: "block" }}>
                  Тип анимации:
                  <select
                    value={anim.type}
                    onChange={(e) => {
                      const newAnimations = [...config.animations.animations];
                      const updatedAnim: SingleAnimation = {
                        ...anim,
                        type: e.target.value as "fade" | "scale" | "slide",
                        // Очищаем несовместимые свойства при смене типа
                        ...(e.target.value === "slide"
                          ? { from: undefined, to: undefined }
                          : { direction: undefined }),
                      };
                      newAnimations[index] = updatedAnim;
                      onConfigChange({
                        ...config,
                        animations: {
                          ...config.animations,
                          animations: newAnimations,
                        },
                      });
                    }}
                    className="form-select"
                    style={{ width: "100%", marginTop: "4px" }}
                  >
                    <option value="fade">fade</option>
                    <option value="scale">scale</option>
                    <option value="slide">slide</option>
                  </select>
                </label>
              </div>

              <div className="form-group" style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "12px", display: "block" }}>
                  Длительность (ms):
                  <input
                    type="number"
                    value={anim.duration || 300}
                    onChange={(e) => {
                      const newAnimations = [...config.animations.animations];
                      newAnimations[index] = {
                        ...anim,
                        duration: parseInt(e.target.value) || undefined,
                      };
                      onConfigChange({
                        ...config,
                        animations: {
                          ...config.animations,
                          animations: newAnimations,
                        },
                      });
                    }}
                    className="form-input"
                    style={{ width: "100%", marginTop: "4px" }}
                    placeholder="300"
                  />
                </label>
              </div>

              <div className="form-group" style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "12px", display: "block" }}>
                  Easing:
                  <select
                    value={anim.easing || "ease-in-out"}
                    onChange={(e) => {
                      const newAnimations = [...config.animations.animations];
                      newAnimations[index] = {
                        ...anim,
                        easing: e.target.value as any,
                      };
                      onConfigChange({
                        ...config,
                        animations: {
                          ...config.animations,
                          animations: newAnimations,
                        },
                      });
                    }}
                    className="form-select"
                    style={{ width: "100%", marginTop: "4px" }}
                  >
                    <option value="ease-in-out">ease-in-out</option>
                    <option value="ease-out">ease-out</option>
                    <option value="ease-in">ease-in</option>
                    <option value="ease">ease</option>
                    <option value="linear">linear</option>
                  </select>
                </label>
              </div>

              {anim.type === "slide" && (
                <div className="form-group" style={{ marginBottom: "12px" }}>
                  <label style={{ fontSize: "12px", display: "block" }}>
                    Направление:
                    <select
                      value={anim.direction || "right"}
                      onChange={(e) => {
                        const newAnimations = [...config.animations.animations];
                        newAnimations[index] = {
                          ...anim,
                          direction: e.target.value as
                            | "left"
                            | "right"
                            | "top"
                            | "bottom",
                        };
                        onConfigChange({
                          ...config,
                          animations: {
                            ...config.animations,
                            animations: newAnimations,
                          },
                        });
                      }}
                      className="form-select"
                      style={{ width: "100%", marginTop: "4px" }}
                    >
                      <option value="right">right</option>
                      <option value="left">left</option>
                      <option value="top">top</option>
                      <option value="bottom">bottom</option>
                    </select>
                  </label>
                </div>
              )}

              {(anim.type === "fade" || anim.type === "scale") && (
                <>
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ fontSize: "12px", display: "block" }}>
                      From:
                      <input
                        type="number"
                        step={anim.type === "fade" ? "0.1" : "0.01"}
                        value={
                          anim.from !== undefined
                            ? anim.from
                            : anim.type === "fade"
                              ? 0
                              : 0.8
                        }
                        onChange={(e) => {
                          const newAnimations = [
                            ...config.animations.animations,
                          ];
                          newAnimations[index] = {
                            ...anim,
                            from: parseFloat(e.target.value) || undefined,
                          };
                          onConfigChange({
                            ...config,
                            animations: {
                              ...config.animations,
                              animations: newAnimations,
                            },
                          });
                        }}
                        className="form-input"
                        style={{ width: "100%", marginTop: "4px" }}
                        placeholder={anim.type === "fade" ? "0" : "0.8"}
                      />
                    </label>
                  </div>
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ fontSize: "12px", display: "block" }}>
                      To:
                      <input
                        type="number"
                        step={anim.type === "fade" ? "0.1" : "0.01"}
                        value={
                          anim.to !== undefined
                            ? anim.to
                            : anim.type === "fade"
                              ? 1
                              : 1
                        }
                        onChange={(e) => {
                          const newAnimations = [
                            ...config.animations.animations,
                          ];
                          newAnimations[index] = {
                            ...anim,
                            to: parseFloat(e.target.value) || undefined,
                          };
                          onConfigChange({
                            ...config,
                            animations: {
                              ...config.animations,
                              animations: newAnimations,
                            },
                          });
                        }}
                        className="form-input"
                        style={{ width: "100%", marginTop: "4px" }}
                        placeholder="1"
                      />
                    </label>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h3>Настройки стилей</h3>
        <div className="styling-editor-header">
          <p className="styling-editor-description">
            Редактируйте объект стилей в формате JSON. Изменения применяются
            автоматически.
          </p>
        </div>
        <div className="styling-editor">
          <Editor
            height="300px"
            theme="vs-dark"
            language="json"
            value={JSON.stringify(config.styling, null, 2)}
            onChange={handleStylingChange}
            options={{
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 14,
              lineNumbers: "on",
              wordWrap: "on",
              automaticLayout: true,
              formatOnPaste: true,
              formatOnType: true,
              tabSize: 2,
              insertSpaces: true,
              bracketPairColorization: { enabled: true },
              guides: {
                bracketPairs: true,
                indentation: true,
              },
            }}
          />
        </div>
        {stylingError && (
          <div className="styling-error">
            <span className="error-icon">⚠️</span>
            <span className="error-message">{stylingError}</span>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Настройки модального окна</h3>
        <div className="form-group">
          <label>
            Выравнивание (alignItems в overlay):
            <select
              value={config.styling?.overlay?.alignItems || "center"}
              onChange={(e) => {
                const newConfig = {
                  ...config,
                  styling: {
                    ...config.styling,
                    overlay: {
                      ...config.styling?.overlay,
                      alignItems: e.target.value,
                    },
                  },
                };
                onConfigChange(newConfig);
              }}
              className="form-select"
            >
              <option value="center">center</option>
              <option value="flex-start">flex-start (top)</option>
              <option value="flex-end">flex-end (bottom)</option>
            </select>
            <small
              style={{ display: "block", marginTop: "4px", color: "#6c757d" }}
            >
              ⚠️ В SDK 1.4.0+ позиция настраивается через
              styling.overlay.alignItems
            </small>
          </label>
        </div>
        <div className="form-group">
          <label>
            Закрывать по клику на backdrop:
            <input
              type="checkbox"
              checked={config.modal.closeOnBackdropClick}
              onChange={(e) =>
                handleNestedInputChange(
                  "modal",
                  "closeOnBackdropClick",
                  e.target.checked
                )
              }
              className="form-checkbox"
            />
          </label>
        </div>
        <div className="form-group">
          <label>
            Закрывать по Escape:
            <input
              type="checkbox"
              checked={config.modal.closeOnEscape}
              onChange={(e) =>
                handleNestedInputChange(
                  "modal",
                  "closeOnEscape",
                  e.target.checked
                )
              }
              className="form-checkbox"
            />
          </label>
        </div>
      </div>
    </div>
  );
};
